import { OpsSeverity, type OpsSignalPolicyRecord } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, NoddiSheet, useAppForm } from 'noddi-ui-web';
import { OPS_KIND_META, OPS_POLICY_SEVERITY_OPTIONS } from 'noddi-util';
import { z } from 'zod';

interface Props {
  policy: OpsSignalPolicyRecord;
  onClose: () => void;
}

const FORM_ID = 'edit-ops-policy';
const schema = z
  .object({
    severity: z.enum(['AUTOMATIC', ...Object.values(OpsSeverity)]),
    defaultHours: z.number().positive().max(720),
    maxHours: z.number().positive().max(720)
  })
  .refine((value) => value.defaultHours <= value.maxHours, {
    path: ['defaultHours'],
    message: 'Suggested duration must not exceed the maximum.'
  });

export const EditOpsSignalPolicySheet = ({ policy, onClose }: Props) => {
  const { mutateAsync: save, error } = noddiAsync.usePost({
    type: URLKeys.postOpsSignalPolicy,
    queryConfig: {
      meta: { suppressErrorToast: true },
      onSuccess: () => {
        void invalidateQuery({ urlKey: URLKeys.getOpsSignalPolicies });
        void invalidateQuery({ urlKey: URLKeys.getOpsSignals });
      }
    }
  });
  const form = useAppForm({
    defaultValues: {
      severity: policy.severityOverride ?? 'AUTOMATIC',
      defaultHours: (policy.snoozeDefaultMinutes ?? 1440) / 60,
      maxHours: (policy.snoozeMaxMinutes ?? 10080) / 60
    } satisfies z.input<typeof schema>,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      await save(
        {
          serviceDepartmentId: policy.serviceDepartmentId,
          kind: policy.kind,
          severityOverride: value.severity === 'AUTOMATIC' ? null : value.severity,
          snoozeDefaultMinutes: Math.round(value.defaultHours * 60),
          snoozeMaxMinutes: Math.round(value.maxHours * 60)
        },
        { onSuccess: onClose }
      );
    }
  });
  return (
    <form.AppForm>
      <NoddiSheet
        open
        title={`Settings: ${OPS_KIND_META[policy.kind].title}`}
        onOpenChange={(open) => !open && onClose()}
        footer={<form.SubscribeButton formId={FORM_ID} label='Save settings' />}
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-5'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <p className='text-sm text-muted-foreground'>
            Changes apply to current and future signals in this department. Automatic Critical signals cannot be
            downgraded.
          </p>
          <form.AppField name='severity'>
            {(field) => <field.Select label='Severity' options={OPS_POLICY_SEVERITY_OPTIONS} />}
          </form.AppField>
          <form.AppField name='defaultHours'>
            {(field) => <field.Input type='number' label='Suggested Snooze (hours)' required />}
          </form.AppField>
          <form.AppField name='maxHours'>
            {(field) => <field.Input type='number' label='Maximum Snooze (hours)' required />}
          </form.AppField>
          <p className='text-sm text-muted-foreground'>
            Up to 30 days. A known operational deadline takes precedence. Critical signals cannot be snoozed.
          </p>
          {error && (
            <div role='alert'>
              <ApiErrorMessageInternal error={error} />
            </div>
          )}
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
